/**
 * Currency and line-item rounding (piece 2: Currency, Line-item rounding;
 * decision 0097). `amount` is always integer cents; rounding is round-half-up
 * to the nearest cent, applied once per line with any discount inside that
 * single rounding, and a document total sums already-rounded cents.
 */

/** A shared monetary value: integer cents and an ISO 4217 alphabetic code. */
export interface Currency {
  readonly amount: number;
  readonly currencyCode: string;
}

/** A percentage discount multiplies the raw cents; a flat discount subtracts whole cents. Both land before the one rounding. */
export type LineDiscount =
  | { readonly kind: 'Percentage'; readonly percent: number }
  | { readonly kind: 'Flat'; readonly amount: Currency };

/** The deployment's one currency today; the code is never left empty as a placeholder. */
export const DEFAULT_CURRENCY_CODE = 'USD';

const ISO_4217_ALPHA = /^[A-Z]{3}$/;

export class CurrencyError extends Error {}

/** Refuses a fractional amount, a missing code, and a code that is not three upper-case letters. */
export function assertCurrency(value: Currency): Currency {
  if (!Number.isInteger(value.amount)) {
    throw new CurrencyError(`Currency.amount stores integer cents; got ${String(value.amount)}`);
  }
  if (typeof value.currencyCode !== 'string' || !ISO_4217_ALPHA.test(value.currencyCode)) {
    throw new CurrencyError('Currency.currencyCode is an ISO 4217 alphabetic code and is never empty');
  }
  return value;
}

/** Builds a Currency in the default code from integer cents. */
export function cents(amount: number, currencyCode: string = DEFAULT_CURRENCY_CODE): Currency {
  return assertCurrency({ amount, currencyCode });
}

/**
 * Round-half-up to the nearest whole cent, away from zero on a negative amount
 * (a credit rounds the way its debit did). Never round-half-to-even.
 */
export function roundHalfUpCents(rawCents: number): number {
  if (!Number.isFinite(rawCents)) throw new CurrencyError('cannot round a non-finite amount');
  const magnitude = Math.floor(Math.abs(rawCents) + 0.5 + 1e-9);
  return rawCents < 0 ? -magnitude : magnitude;
}

/** A dollar figure to the cent, half up and the same way for a credit (T2192 item 12; T535): the one `round2`, never a local copy. */
export function round2(n: number): number {
  return Number.isFinite(n) ? roundHalfUpCents(n * 100) / 100 : n;
}

/** A quantity or factor to four places with an epsilon nudge so a binary tie rounds up; the one `round4`, never a local copy (T678). */
export function round4(n: number): number {
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
}

/**
 * A stored decimal read as whole cents. 🔨 One definition, not one per service
 * (found in review, 2026-09-15): four copies stood, two of them on
 * `Math.round`, which is not the half-up rule the ledger keeps. A Prisma
 * `Decimal` arrives as an object carrying `toString`, so the value is read
 * through that rather than through a cast, and absent reads as nothing.
 */
export function toCents(value: { toString(): string } | null | undefined): number {
  return value === null || value === undefined ? 0 : roundHalfUpCents(Number(value.toString()) * 100);
}

/**
 * The line amount: multiply, apply the discount to the raw cents, then round
 * once. Rounding the amount first and then discounting is the forbidden order.
 */
export function computeLineAmount(qty: number, rate: Currency, discount?: LineDiscount): Currency {
  assertCurrency(rate);
  if (!Number.isFinite(qty)) throw new CurrencyError('quantity must be a finite number');
  let raw = qty * rate.amount;
  if (discount) {
    if (discount.kind === 'Percentage') {
      if (discount.percent < 0 || discount.percent > 100) throw new CurrencyError('a percentage discount is between 0 and 100');
      raw = raw * (1 - discount.percent / 100);
    } else {
      assertCurrency(discount.amount);
      if (discount.amount.currencyCode !== rate.currencyCode) throw new CurrencyError('a flat discount is in the line\'s currency');
      raw = raw - discount.amount.amount;
    }
  }
  return { amount: roundHalfUpCents(raw), currencyCode: rate.currencyCode };
}

/**
 * Per-line tax against that line's own rounded amount, rounded once. `taxRate`
 * is a fraction (0.06 for six percent). This function resolves no rate of its
 * own; Tax and compliance's `calculateTax` passes one in.
 */
export function computeLineTax(lineAmount: Currency, taxRate: number): Currency {
  assertCurrency(lineAmount);
  if (!Number.isFinite(taxRate) || taxRate < 0) throw new CurrencyError('a tax rate is a non-negative fraction');
  return { amount: roundHalfUpCents(lineAmount.amount * taxRate), currencyCode: lineAmount.currencyCode };
}

/** T2962: one formatter per code and decimals, built once and held, as `cachedFormatter` holds a date's. */
const currencyFormatters = new Map<string, Intl.NumberFormat>();
function currencyFormatter(code: string, decimals?: number): Intl.NumberFormat {
  const key = `${code}|${decimals ?? ''}`;
  let held = currencyFormatters.get(key);
  if (!held) {
    held = decimals === undefined
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: code })
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    currencyFormatters.set(key, held);
  }
  return held;
}

/**
 * A Currency as a person reads it: the code's own symbol, thousands grouped and
 * both cents shown. 🔨 Stored cents never reach a screen raw — a limit written
 * as 1000000 reads as $10,000.00 here and as ten million anywhere else.
 */
export function formatCurrency(value: Currency): string {
  assertCurrency(value);
  return currencyFormatter(value.currencyCode).format(value.amount / 100);
}

/** Whole cents and a code as the money text the screens draw; the phone's lists and the web's tiles read it (T3214). */
export function formatCentsAmount(amountCents: number, currencyCode: string = DEFAULT_CURRENCY_CODE): string {
  return formatCurrency(cents(amountCents, currencyCode));
}

/**
 * A rate per unit as a person reads it (T1621): dollars with a fixed number of
 * decimals, three by default, so the mileage rate stored as 70 cents a mile reads
 * `$0.700`. The stored value stays whole cents; only the reading carries the places.
 */
export function formatCurrencyRate(value: Currency, decimals = 3): string {
  assertCurrency(value);
  return currencyFormatter(value.currencyCode, decimals).format(value.amount / 100);
}

/**
 * A rate kept in cents with a fraction of a cent (decision 13070: a mileage rate stored to four places of a cent),
 * read as money to the places asked for, six by default, so 54.5001 cents a mile reads `$0.545001`. A `Currency`
 * holds whole cents, so this takes the raw figure and the code and asserts nothing of the amount.
 */
export function formatRateCents(rateCents: number, currencyCode: string = DEFAULT_CURRENCY_CODE, decimals = 6): string {
  if (!Number.isFinite(rateCents)) throw new CurrencyError('a rate is a finite number of cents');
  return currencyFormatter(currencyCode, decimals).format(rateCents / 100);
}

/** Sums already-rounded cents; there is no second rounding pass at the document total. */
export function computeDocumentTotal(lines: readonly Currency[], currencyCode: string = DEFAULT_CURRENCY_CODE): Currency {
  let total = 0;
  for (const line of lines) {
    assertCurrency(line);
    if (line.currencyCode !== currencyCode) throw new CurrencyError('every line of a document is in the document\'s currency');
    total += line.amount;
  }
  return { amount: total, currencyCode };
}

/**
 * A percentage as a person reads it (T660): at most `maxDecimals` places, default one, with trailing
 * zeros trimmed (`6.25` and `6`, never `6.250`), and a dash where there is no value.
 */
export function formatPercent(value: number | null, options: { readonly maxDecimals?: number } = {}): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${Number(value.toFixed(options.maxDecimals ?? 1))}%`;
}
